/**
 * Feuille « grand format » — 2 colonnes, sections + descriptions. Portée
 * depuis menu-grand-format.html (`.sheet` / `.cols` / `.sec` / `.row`,
 * fonctions `sheet()` + `col()`). Composant unique paramétré par une
 * `LargePalette` : les 3 styles (Éditorial / Boudoir / Pop) ne diffèrent que
 * par les couleurs (cf `largeTemplates.tsx` qui les instancie).
 *
 * Styles inline (pas de Tailwind) : ce noeud est la cible directe de
 * `domToPng` — la palette est FIXE, déconnectée du thème clair/sombre du
 * cockpit.
 *
 * Largeur de conception : 600px. Le conteneur appelant peut scroller
 * horizontalement si l'espace manque ; l'export capture le noeud à sa taille
 * pleine (hauteur incluse — pas de coupe).
 */
import { forwardRef, type CSSProperties } from "react";

import type { MenuSheetData, MenuSheetSection } from "../types";
import { PortraitPlaceholder } from "./PortraitPlaceholder";
import type { LargePalette } from "./largePalettes";

/** Une colonne de sections (titre + lignes prix). Partagée avec
 *  `WideSheet.tsx` (layout wide, 2-3 colonnes) — même rendu de colonne, le
 *  nombre de colonnes et la largeur de la feuille changent, pas le contenu
 *  d'une colonne. */
export function SheetColumn({ sections, palette }: { sections: MenuSheetSection[]; palette: LargePalette }) {
  return (
    <div>
      {sections.map((section, sIdx) => (
        <div key={section.id} style={{ marginTop: sIdx === 0 ? 0 : 16 }}>
          <p
            style={{
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontWeight: 500,
              margin: "0 0 8px",
              paddingBottom: 5,
              color: palette.sectionTitleColor,
              borderBottom: `1px solid ${palette.sectionTitleBorder}`,
            }}
          >
            {section.title}
          </p>
          {section.items.map((item, i) => (
            <div
              key={item.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                alignItems: "baseline",
                gap: 12,
                padding: "6px 0",
                borderTop: i === 0 ? "none" : `1px solid ${palette.rowLine}`,
              }}
            >
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{item.name}</div>
                {item.description && (
                  <div style={{ fontSize: 11, lineHeight: 1.4, marginTop: 1, color: palette.descColor }}>
                    {item.description}
                  </div>
                )}
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                  color: palette.priceColor,
                }}
              >
                {item.price}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Feuille grand format paramétrée par palette. La ref pointe le noeud racine
 *  (cible `domToPng`). */
export const LargeSheetView = forwardRef<
  HTMLDivElement,
  { data: MenuSheetData; palette: LargePalette }
>(function LargeSheetView({ data, palette }, ref) {
  const titleStyle: CSSProperties = {
    fontFamily: "Georgia, serif",
    fontWeight: 400,
    letterSpacing: palette.titleUppercase ? "0.02em" : "-0.01em",
    fontSize: 40,
    lineHeight: 0.95,
    margin: 0,
    textTransform: palette.titleUppercase ? "uppercase" : "none",
  };
  return (
    <div
      ref={ref}
      style={{
        boxSizing: "border-box",
        width: "100%",
        borderRadius: 14,
        overflow: "hidden",
        padding: "34px 34px 28px",
        background: palette.background,
        color: palette.color,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
        <div>
          <h2 style={titleStyle}>{data.name}</h2>
        </div>
        <div
          style={{
            width: 118,
            height: 118,
            borderRadius: 10,
            overflow: "hidden",
            flex: "none",
            border: `1px solid ${palette.portraitBorder}`,
          }}
        >
          {data.photoUrl ? (
            <img
              src={data.photoUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <PortraitPlaceholder bg={palette.portraitBg} fg={palette.portraitFg} shape="square" />
          )}
        </div>
      </div>

      <div style={{ height: 1, margin: "20px 0 4px", background: palette.ruleColor }} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "26px 34px" }}>
        <SheetColumn sections={data.columns[0]} palette={palette} />
        <SheetColumn sections={data.columns[1]} palette={palette} />
      </div>

      <div
        style={{
          marginTop: 22,
          textAlign: "center",
          fontSize: 10,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: palette.footColor,
        }}
      >
        Dis-moi ce qui te tente · je m'occupe du reste 💌
      </div>
    </div>
  );
});
